import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons";

export const Navbar = () => {
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [eventsDropdownOpen, setEventsDropdownOpen] = useState(false);
  const [layananDropdownOpen, setLayananDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("");
  const auth = getAuth();
  const menuRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error saat logout:", error.message);
    }
  };

  const handleHamburgerClick = () => {
    setMenuOpen(!menuOpen);
  };

  const handleMenuItemClick = (action) => {
    if (action === 'toggleProfile') {
      setDropdownOpen(!dropdownOpen);
    } else if (action === 'navigate') {
      setMenuOpen(false);
      setDropdownOpen(false);
    }
  };

  return (
    <nav className="w-full sticky-navbar mt-6 py-4 bg-slate-100 shadow-lg text-cyan-800">
      <div className="container mx-auto px-4 flex items-center justify-between" ref={menuRef}>

        {/* 🔹 Logo & Teks di Samping (Hanya Desktop) */}
        <div className="flex-1 md:flex md:items-center">
          <Link to="/" className="flex items-center space-x-4 text-2xl font-bold mx-auto md:mx-0">
            <img src="/logoCyan.svg" alt="Logo" className="w-14 h-auto md:w-14 lg:w-14" />

            <span className="hidden md:block text-2xl font-black text-cyan-600">
              CAKRAWALA JAKARTA
            </span>
          </Link>
        </div>

        {/* 🔹 Teks "ALTI DKI JAKARTA" di Tengah (Mobile) */}
        <span className="block md:hidden absolute left-1/2 transform -translate-x-1/2 text-base font-semibold oneline whitespace-nowrap">
          CAKRAWALA JAKARTA
        </span>

        {/* 🔹 Menu (Desktop) */}
        <div className="hidden md:flex items-center space-x-8 md:space-x-4 text-base uppercase font-medium px-8 md:px-04">
          <Link
            to="/"
            className={`hover:text-cyan-600 ${activeMenu === "beranda" ? "border-b-2 border-cyan-600" : ""}`}
            onClick={() => setActiveMenu("beranda")}
          >
            BERANDA
          </Link>

          <Link
            to="/KartuAkses"
            className={`hover:text-cyan-600 ${activeMenu === "kartuakses" ? "border-b-2 border-cyan-600" : ""}`}
            onClick={() => setActiveMenu("kartuakses")}
          >
            KARTU AKSES
          </Link>

          <Link
            to="/Kontak"
            className={`hover:text-cyan-600 ${activeMenu === "kontak" ? "border-b-2 border-cyan-600" : ""}`}
            onClick={() => setActiveMenu("kontak")}
          >
            KONTAK
          </Link>
        </div>

        {/* 🔹 Login / Avatar */}
        <div className="hidden md:flex items-center space-x-4">
          {user ? (
            <div
              className="relative flex items-center space-x-4"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <div className="w-10 h-10 rounded-full overflow-hidden">
                <img
                  src={user.photoURL || "https://ui-avatars.com/api/?name=User"}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              </div>

              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-full mt-8 w-52 bg-slate-100 shadow-lg rounded-lg overflow-hidden"
                  >
                    <Link to="/Profile" className="block px-4 py-2 text-cyan-800 hover:bg-slate-200 hover:text-cyan-600">
                      My Profile
                    </Link>
                    <Link to="/PesananSaya" className="block px-4 py-2 text-cyan-800 hover:bg-slate-200 hover:text-cyan-600">
                      Pesanan Saya
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-cyan-800 hover:bg-slate-200"
                    >
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

            </div>
          ) : (
            <Link to="/Login" className="bg-cyan-600 text-white px-5 py-2 ml-6 rounded-lg hover:bg-cyan-800">
              Login
            </Link>
          )}
        </div>

        {/* 🔹 Tombol Menu Mobile */}
        <button className="md:hidden focus:outline-none" onClick={handleHamburgerClick}>
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
            )}
          </svg>
        </button>

      </div>

      {/* 🔹 Dropdown Menu untuk Mobile */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 w-full bg-slate-100 shadow-lg md:hidden min-h-screen"
          >
            <div className="flex flex-col text-center w-full px-4 uppercase font-medium divide-y divide-slate-200 max-w-lg mx-auto">
              <div className="flex flex-col py-4 space-y-6">
                {[
                  { to: "/", text: "BERANDA" },
                  { to: "/KartuAkses", text: "KARTU AKSES" },
                  { to: "/Kontak", text: "KONTAK" }
                ].map((item, index) => (
                  <motion.div
                    key={item.text}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="w-full"
                  >
                    <Link
                      to={item.to}
                      className="hover:text-cyan-600 transition-colors block"
                      onClick={() => handleMenuItemClick('navigate')}
                    >
                      {item.text}
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Profile Related Links */}
              {user ? (
                <div className="flex flex-col py-4 space-y-6">
                  {[
                    { to: "/Profile", text: "MY PROFILE" },
                    { to: "/PesananSaya", text: "PESANAN SAYA" }
                  ].map((item, index) => (
                    <motion.div
                      key={item.text}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: (index + 5) * 0.1 }}
                      className="w-full"
                    >
                      <Link
                        to={item.to}
                        className="text-cyan-800 hover:text-cyan-600 transition-colors block"
                        onClick={() => handleMenuItemClick('navigate')}
                      >
                        {item.text}
                      </Link>
                    </motion.div>
                  ))}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.7 }}
                    className="w-full"
                  >
                    <button
                      onClick={() => {
                        handleLogout();
                        handleMenuItemClick('navigate');
                      }}
                      className="text-red-600 hover:text-red-700 transition-colors flex items-center justify-center gap-2 w-full"
                    >
                      <span>LOGOUT</span>
                      <FontAwesomeIcon icon={faSignOutAlt} />
                    </button>
                  </motion.div>
                </div>
              ) : (
                <div className="py-4">
                  <Link
                    to="/Login"
                    className="text-cyan-800 hover:text-cyan-600 block"
                    onClick={() => handleMenuItemClick('navigate')}
                  >
                    LOGIN
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>




    </nav>
  );
};

